import { BACKEND_URL } from "./utils/backendUrl";
import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Menu, X, ArrowRight, ChevronRight, Layers, Zap, Shield, Droplets, Thermometer, Scale, Eye, DollarSign, Heart, BarChart3, Calculator, BookOpen, Star, Check, ChevronLeft, Sparkles, Wrench, Sun, FlaskConical, Gauge, Move, Box, Cpu, Moon, Search, User, LogOut, LogIn, UserPlus, Mic, MicOff, Loader2, MessageCircle, Stethoscope, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

// Extracted contexts and components
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import VoiceAssistant from "@/components/VoiceAssistant";
import PropertyBar from "@/components/PropertyBar";
import { MaterialCard } from "@/components/MaterialCard";
import BrandPage from "@/components/BrandPage";
import CatalogPage from "@/pages/CatalogPage";
import AdminPage from "@/pages/AdminPage";
import CalculatorPage from "@/pages/CalculatorPage";
import { getBuyUrl, getBuyUrls, trackBuyClick, trackAndNavigate } from "@/utils/buyUrl";

// Re-exports kept for backward compatibility
export { useTheme, useAuth };
export { MaterialCard };

const API = `${BACKEND_URL}/api`;

// Home Page
const Home = () => {
  const navigate = useNavigate();
  const [brandClicks, setBrandClicks] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/brand-clicks`);
        if (!cancelled) setBrandClicks(response.data.by_brand || {});
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('brand-clicks poll failed:', err.message);
        }
      }
    };
    fetchStats();
    // Refresh every 30s for "live" feel
    const interval = setInterval(fetchStats, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section px-6 max-w-7xl mx-auto">
        <div className="space-y-8 animate-fade-in-up">
          <div>
            <Badge variant="outline" className="badge-industrial mb-4">
              <Sparkles className="w-3 h-3 mr-2" />
              Sistema de Recomendación
            </Badge>
            <h1 className="text-5xl lg:text-7xl leading-none mb-4">
              ENCUENTRA EL<br />
              <span className="text-[#1a8ccc]">MATERIAL</span><br />
              PERFECTO
            </h1>
            <p className="text-lg text-muted-foreground max-w-md">
              Herramienta inteligente para seleccionar el material de impresión 3D óptimo según tus requerimientos técnicos.
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button
              size="lg"
              className="btn-industrial rounded-sm px-8 py-6 bg-[#1a8ccc] hover:bg-[#1575b0] text-white"
              onClick={() => navigate("/wizard")}
              data-testid="cta-wizard-btn"
            >
              INICIAR ASISTENTE
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="btn-industrial rounded-sm px-8 py-6 border-[#1a8ccc] text-[#1a8ccc] hover:bg-[#1a8ccc] hover:text-white"
              onClick={() => navigate("/quick")}
              data-testid="cta-quick-btn"
            >
              BÚSQUEDA RÁPIDA
            </Button>
          </div>
        </div>

        <div className="relative animate-fade-in-up stagger-2 hidden lg:block">
          <div className="aspect-square bg-card border border-border overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1739169169463-450148af26ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
              alt="3D Printing Filament"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-4 -left-4 bg-background border border-border p-4">
            <p className="font-mono text-sm">+36 MATERIALES</p>
            <p className="text-xs text-muted-foreground">SICNOVA · BAMBU LAB · FORMLABS · eSUN</p>
          </div>
        </div>
      </section>

      {/* Brand Partners Section */}
      <section className="py-16 px-6 max-w-7xl mx-auto" data-testid="brands-section">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl">MARCAS ALIADAS</h2>
          <Badge variant="outline" className="badge-industrial bg-[#00a651]/10 border-[#00a651] text-[#00a651]">
            <Sparkles className="w-3 h-3 mr-2" />
            NUEVA COLABORACIÓN
          </Badge>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: "eSUN", count: 10, color: "#00a651", desc: "PLA+, PETG, ABS+, TPU, eSilk, eMarble, ePLA-CF, ePA, ePC", highlight: true, slug: "eSUN", clickKey: "esun" },
            { name: "Bambu Lab", count: 5, color: "#1a8ccc", desc: "PLA, ABS, PETG, TPU, ASA — vía SICNOVA", slug: "bambu", clickKey: "bambulab" },
            { name: "Formlabs", count: 7, color: "#7e57c2", desc: "Resinas SLA biocompatibles, dentales y técnicas", slug: "formlabs", clickKey: "formlabs" },
            { name: "SICNOVA", count: 5, color: "#f59e0b", desc: "Filamentos PLA Tough, Markforged, BASF, Polymaker", slug: "sicnova", clickKey: "sicnova" },
          ].map((brand) => {
            const stats = brandClicks[brand.clickKey] || { today: 0, total: 0 };
            return (
            <Link
              key={brand.name}
              to={`/brand/${brand.slug}`}
              className={`group relative p-6 border transition-all hover:shadow-lg ${brand.highlight ? "bg-[#00a651]/5" : "bg-card"}`}
              data-testid={`brand-card-${brand.slug.toLowerCase()}`}
              style={{ borderColor: brand.highlight ? brand.color : undefined }}
            >
              {brand.highlight && (
                <div className="absolute -top-3 right-3 bg-[#00a651] text-white text-xs font-bold px-2 py-1 rounded-sm">
                  NUEVO
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold tracking-tight" style={{ color: brand.color }}>
                  {brand.name}
                </h3>
                <span className="font-mono text-xs text-muted-foreground">
                  {brand.count} {brand.count === 1 ? "material" : "materiales"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">{brand.desc}</p>

              {/* Live click counters */}
              <div className="flex items-center gap-3 mt-4 mb-3 text-xs" data-testid={`brand-stats-${brand.clickKey}`}>
                <div className="flex items-center gap-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: brand.color }}></span>
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: brand.color }}></span>
                  </span>
                  <span className="font-mono font-medium" style={{ color: brand.color }}>
                    {stats.today}
                  </span>
                  <span className="text-muted-foreground">visitas hoy</span>
                </div>
                {stats.total > 0 && (
                  <span className="text-muted-foreground font-mono">· {stats.total} totales</span>
                )}
              </div>

              <div className="flex items-center gap-1 text-xs font-medium" style={{ color: brand.color }}>
                VER CATÁLOGO
                <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <h2 className="text-3xl mb-12">CÓMO FUNCIONA</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <BookOpen className="w-8 h-8" />,
              title: "1. DEFINE TUS REQUISITOS",
              description: "Responde preguntas sobre el uso de tu pieza y sus requerimientos mecánicos, químicos y ambientales."
            },
            {
              icon: <Zap className="w-8 h-8" />,
              title: "2. ANÁLISIS INTELIGENTE",
              description: "Nuestro algoritmo evalúa los materiales disponibles y calcula cuáles se adaptan mejor."
            },
            {
              icon: <Star className="w-8 h-8" />,
              title: "3. RECOMENDACIÓN PRECISA",
              description: "Recibe una lista ordenada de materiales con toda la información técnica necesaria."
            }
          ].map((feature, idx) => (
            <Card key={idx} className="border-border p-8 hover:border-[#1a8ccc] transition-colors">
              <div className="text-[#1a8ccc] mb-4">{feature.icon}</div>
              <h3 className="text-lg mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 px-6 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-px bg-border">
            {[
              { icon: <Layers />, label: "Catálogo", path: "/catalog", desc: "Ver todos los materiales" },
              { icon: <BarChart3 />, label: "Comparador", path: "/compare", desc: "Comparar hasta 3" },
              { icon: <Calculator />, label: "Calculadora", path: "/calculator", desc: "Estimar costos" },
              { icon: <Wrench />, label: "Asistente", path: "/wizard", desc: "Guía paso a paso" }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className="bg-background p-8 flex flex-col items-start hover:bg-muted transition-colors group"
                data-testid={`quick-link-${item.path.replace("/", "")}`}
              >
                <div className="text-muted-foreground group-hover:text-foreground transition-colors mb-4">
                  {item.icon}
                </div>
                <span className="font-bold text-lg mb-1">{item.label}</span>
                <span className="text-sm text-muted-foreground">{item.desc}</span>
                <ChevronRight className="w-5 h-5 mt-4 text-muted-foreground group-hover:text-[#1a8ccc] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <div className="App app-container noise-bg">
            <BrowserRouter>
              <Navigation />
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/wizard" element={<WizardPage />} />
                  <Route path="/quick" element={<QuickSearchPage />} />
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/calculator" element={<CalculatorPage />} />
                  <Route path="/results" element={<ResultsPage />} />
                  <Route path="/material/:id" element={<MaterialDetailPage />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/brand/:slug" element={<BrandPage />} />
                </Routes>
              </main>
              <VoiceAssistant />
              <Toaster position="bottom-right" richColors />
            </BrowserRouter>
          </div>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Wizard Page Component
const WizardPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    application_type: null,
    min_tensile_strength: 0,
    min_impact_resistance: 0,
    min_hardness: 0,
    min_flexibility: 0,
    min_rigidity: 0,
    min_uv_resistance: 0,
    min_moisture_resistance: 0,
    min_chemical_resistance: 0,
    min_heat_resistance: 0,
    needs_conductivity: false,
    needs_transparency: false,
    preferred_weight: null,
    max_price: null
  });

  const totalSteps = 4;

  const applicationTypes = [
    { id: "functional", label: "Pieza Funcional", icon: <Wrench />, desc: "Engranajes, soportes, herramientas" },
    { id: "decorative", label: "Decorativa", icon: <Star />, desc: "Figuras, arte, maquetas" },
    { id: "prototype", label: "Prototipo", icon: <Box />, desc: "Pruebas de diseño, iteraciones" },
    { id: "mechanical", label: "Mecánica", icon: <Cpu />, desc: "Alta carga, precisión" },
    { id: "outdoor", label: "Exterior", icon: <Sun />, desc: "Exposición UV, intemperie" },
    { id: "food_safe", label: "Contacto Alimentario", icon: <FlaskConical />, desc: "Certificado FDA" },
    { id: "dental", label: "Dental", icon: <Activity />, desc: "Férulas, modelos, guías" },
    { id: "medical", label: "Médico", icon: <Stethoscope />, desc: "Dispositivos biocompatibles" }
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/recommend`, formData);
      navigate("/results", { state: { recommendations: response.data.recommendations } });
    } catch (error) {
      toast.error("Error al obtener recomendaciones");
    } finally {
      setLoading(false);
    }
  };

  const StepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3, 4].map((s, idx) => (
        <React.Fragment key={s}>
          <div
            className={`step-dot ${s === step ? "active" : s < step ? "completed" : ""}`}
            data-testid={`step-indicator-${s}`}
          >
            {s < step ? <Check className="w-4 h-4" /> : s}
          </div>
          {idx < 3 && <div className={`step-line ${s < step ? "completed" : ""}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen pt-20" data-testid="wizard-page">
      <div className="wizard-container">
        {/* Form Side */}
        <div className="wizard-form">
          <StepIndicator />

          {/* Step 1: Application Type */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in-up" data-testid="wizard-step-1">
              <div>
                <h2 className="text-3xl mb-2">TIPO DE APLICACIÓN</h2>
                <p className="text-muted-foreground">¿Para qué usarás la pieza impresa?</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {applicationTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`option-card ${formData.application_type === type.id ? "selected" : ""}`}
                    onClick={() => setFormData({ ...formData, application_type: type.id })}
                    data-testid={`app-type-${type.id}`}
                  >
                    <div className="text-muted-foreground mb-2">{type.icon}</div>
                    <h4 className="font-bold mb-1">{type.label}</h4>
                    <p className="text-xs text-muted-foreground">{type.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Mechanical Properties */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in-up" data-testid="wizard-step-2">
              <div>
                <h2 className="text-3xl mb-2">PROPIEDADES MECÁNICAS</h2>
                <p className="text-muted-foreground">Define los requerimientos de resistencia</p>
              </div>
              <div className="space-y-8">
                {[
                  { key: "min_tensile_strength", label: "Resistencia a Tracción", icon: <Gauge /> },
                  { key: "min_impact_resistance", label: "Resistencia a Impactos", icon: <Shield /> },
                  { key: "min_hardness", label: "Dureza", icon: <Layers /> },
                  { key: "min_flexibility", label: "Flexibilidad", icon: <Move /> },
                  { key: "min_rigidity", label: "Rigidez", icon: <Box /> }
                ].map((prop) => (
                  <div key={prop.key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{prop.icon}</span>
                        <Label>{prop.label}</Label>
                      </div>
                      <span className="font-mono">{formData[prop.key]}</span>
                    </div>
                    <Slider
                      value={[formData[prop.key]]}
                      onValueChange={([val]) => setFormData({ ...formData, [prop.key]: val })}
                      max={10}
                      step={1}
                      className="w-full"
                      data-testid={`slider-${prop.key}`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Mínimo</span>
                      <span>Máximo</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Environmental Properties */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in-up" data-testid="wizard-step-3">
              <div>
                <h2 className="text-3xl mb-2">PROPIEDADES AMBIENTALES</h2>
                <p className="text-muted-foreground">Condiciones de uso y exposición</p>
              </div>
              <div className="space-y-8">
                {[
                  { key: "min_uv_resistance", label: "Resistencia UV", icon: <Sun /> },
                  { key: "min_moisture_resistance", label: "Resistencia a Humedad", icon: <Droplets /> },
                  { key: "min_chemical_resistance", label: "Resistencia Química", icon: <FlaskConical /> },
                  { key: "min_heat_resistance", label: "Resistencia Térmica", icon: <Thermometer /> }
                ].map((prop) => (
                  <div key={prop.key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{prop.icon}</span>
                        <Label>{prop.label}</Label>
                      </div>
                      <span className="font-mono">{formData[prop.key]}</span>
                    </div>
                    <Slider
                      value={[formData[prop.key]]}
                      onValueChange={([val]) => setFormData({ ...formData, [prop.key]: val })}
                      max={10}
                      step={1}
                      className="w-full"
                      data-testid={`slider-${prop.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Other Requirements */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in-up" data-testid="wizard-step-4">
              <div>
                <h2 className="text-3xl mb-2">OTROS REQUISITOS</h2>
                <p className="text-muted-foreground">Propiedades adicionales y presupuesto</p>
              </div>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <Label>Conductividad Eléctrica</Label>
                      <p className="text-xs text-muted-foreground">Material conductor o disipativo</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.needs_conductivity}
                    onCheckedChange={(val) => setFormData({ ...formData, needs_conductivity: val })}
                    data-testid="switch-conductivity"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <Label>Transparencia</Label>
                      <p className="text-xs text-muted-foreground">Material translúcido o transparente</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.needs_transparency}
                    onCheckedChange={(val) => setFormData({ ...formData, needs_transparency: val })}
                    data-testid="switch-transparency"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Peso Preferido</Label>
                  <Select
                    value={formData.preferred_weight || ""}
                    onValueChange={(val) => setFormData({ ...formData, preferred_weight: val || null })}
                  >
                    <SelectTrigger data-testid="select-weight">
                      <SelectValue placeholder="Sin preferencia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Ligero</SelectItem>
                      <SelectItem value="medium">Medio</SelectItem>
                      <SelectItem value="heavy">Pesado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Precio Máximo (€/kg)</Label>
                  <Input
                    type="number"
                    placeholder="Sin límite"
                    value={formData.max_price || ""}
                    onChange={(e) => setFormData({ ...formData, max_price: e.target.value ? parseFloat(e.target.value) : null })}
                    data-testid="input-max-price"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-8 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="btn-industrial rounded-sm"
              data-testid="wizard-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              ANTERIOR
            </Button>
            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                className="btn-industrial rounded-sm bg-[#1a8ccc] hover:bg-[#1575b0] text-white"
                data-testid="wizard-next-btn"
              >
                SIGUIENTE
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-industrial rounded-sm bg-[#1a8ccc] text-white hover:bg-[#1575b0]"
                data-testid="wizard-submit-btn"
              >
                {loading ? "ANALIZANDO..." : "OBTENER RECOMENDACIONES"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Preview Side */}
        <div className="wizard-preview hidden lg:block">
          <h3 className="text-xl mb-6">RESUMEN DE REQUISITOS</h3>
          <div className="space-y-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Aplicación</p>
              <p className="font-mono">
                {formData.application_type
                  ? applicationTypes.find(t => t.id === formData.application_type)?.label
                  : "Sin seleccionar"}
              </p>
            </div>
            <Separator />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">Propiedades Mecánicas</p>
              <div className="space-y-3">
                <PropertyBar value={formData.min_tensile_strength} label="Tracción" />
                <PropertyBar value={formData.min_impact_resistance} label="Impactos" />
                <PropertyBar value={formData.min_hardness} label="Dureza" />
                <PropertyBar value={formData.min_flexibility} label="Flexibilidad" />
                <PropertyBar value={formData.min_rigidity} label="Rigidez" />
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">Propiedades Ambientales</p>
              <div className="space-y-3">
                <PropertyBar value={formData.min_uv_resistance} label="UV" />
                <PropertyBar value={formData.min_moisture_resistance} label="Humedad" />
                <PropertyBar value={formData.min_chemical_resistance} label="Químico" />
                <PropertyBar value={formData.min_heat_resistance} label="Térmico" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Search Page
const QuickSearchPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    application_type: null,
    min_tensile_strength: 5,
    min_impact_resistance: 5,
    min_hardness: 5,
    min_flexibility: 3,
    min_rigidity: 5,
    min_uv_resistance: 3,
    min_moisture_resistance: 3,
    min_chemical_resistance: 3,
    min_heat_resistance: 5,
    needs_conductivity: false,
    needs_transparency: false,
    preferred_weight: null,
    max_price: null
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/recommend`, formData);
      navigate("/results", { state: { recommendations: response.data.recommendations } });
    } catch (error) {
      toast.error("Error al obtener recomendaciones");
    } finally {
      setLoading(false);
    }
  };

  const allProperties = [
    { key: "min_tensile_strength", label: "Resistencia Tracción", icon: <Gauge className="w-4 h-4" /> },
    { key: "min_impact_resistance", label: "Resistencia Impactos", icon: <Shield className="w-4 h-4" /> },
    { key: "min_hardness", label: "Dureza", icon: <Layers className="w-4 h-4" /> },
    { key: "min_flexibility", label: "Flexibilidad", icon: <Move className="w-4 h-4" /> },
    { key: "min_rigidity", label: "Rigidez", icon: <Box className="w-4 h-4" /> },
    { key: "min_uv_resistance", label: "Resistencia UV", icon: <Sun className="w-4 h-4" /> },
    { key: "min_moisture_resistance", label: "Resistencia Humedad", icon: <Droplets className="w-4 h-4" /> },
    { key: "min_chemical_resistance", label: "Resistencia Química", icon: <FlaskConical className="w-4 h-4" /> },
    { key: "min_heat_resistance", label: "Resistencia Térmica", icon: <Thermometer className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="quick-search-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl mb-4">BÚSQUEDA RÁPIDA</h1>
          <p className="text-muted-foreground">Ajusta los sliders según tus necesidades y obtén recomendaciones instantáneas</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Propiedades Mecánicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {allProperties.slice(0, 5).map((prop) => (
                <div key={prop.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {prop.icon}
                      <Label className="text-sm">{prop.label}</Label>
                    </div>
                    <span className="font-mono text-sm">{formData[prop.key]}</span>
                  </div>
                  <Slider
                    value={[formData[prop.key]]}
                    onValueChange={([val]) => setFormData({ ...formData, [prop.key]: val })}
                    max={10}
                    step={1}
                    data-testid={`quick-slider-${prop.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle>Propiedades Ambientales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {allProperties.slice(5).map((prop) => (
                <div key={prop.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {prop.icon}
                      <Label className="text-sm">{prop.label}</Label>
                    </div>
                    <span className="font-mono text-sm">{formData[prop.key]}</span>
                  </div>
                  <Slider
                    value={[formData[prop.key]]}
                    onValueChange={([val]) => setFormData({ ...formData, [prop.key]: val })}
                    max={10}
                    step={1}
                    data-testid={`quick-slider-${prop.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex justify-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-industrial rounded-sm px-12 py-6 bg-[#1a8ccc] hover:bg-[#1575b0] text-white"
            data-testid="quick-search-submit"
          >
            {loading ? "BUSCANDO..." : "BUSCAR MATERIALES"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Results Page
const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const recommendations = location.state?.recommendations || [];

  if (!recommendations.length) {
    return (
      <div className="min-h-screen pt-24 px-6 flex flex-col items-center justify-center" data-testid="results-empty">
        <h2 className="text-2xl mb-4">No hay resultados</h2>
        <p className="text-muted-foreground mb-8">Usa el asistente para obtener recomendaciones</p>
        <Button onClick={() => navigate("/wizard")} className="btn-industrial rounded-sm bg-[#1a8ccc] hover:bg-[#1575b0] text-white">
          IR AL ASISTENTE
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="results-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <Badge variant="outline" className="badge-industrial mb-4">
            <Check className="w-3 h-3 mr-2" />
            {recommendations.length} materiales encontrados
          </Badge>
          <h1 className="text-4xl mb-4">MATERIALES RECOMENDADOS</h1>
          <p className="text-muted-foreground">Ordenados por compatibilidad con tus requisitos</p>
        </div>

        <div className="space-y-6">
          {recommendations.map((material, idx) => (
            <Card
              key={material.id}
              className={`border-border p-6 cursor-pointer hover:border-[#1a8ccc] transition-colors ${idx === 0 ? "border-[#1a8ccc] border-2" : ""}`}
              data-testid={`result-card-${material.id}`}
              onClick={() => navigate(`/material/${material.id}`)}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-shrink-0">
                  <div className={`w-16 h-16 flex items-center justify-center ${idx === 0 ? "bg-[#1a8ccc] text-white" : "bg-muted"}`}>
                    <span className="font-mono text-2xl font-bold">#{idx + 1}</span>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl">{material.name}</h3>
                      <p className="text-sm text-muted-foreground font-mono">{material.brand}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl">€{material.price_per_kg}/kg</div>
                      <Badge className="mt-1">{material.category}</Badge>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-6">{material.description}</p>
                  
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <PropertyBar value={material.tensile_strength} label="Resistencia" />
                    <PropertyBar value={material.flexibility} label="Flexibilidad" />
                    <PropertyBar value={material.heat_resistance} label="Temperatura" />
                    <PropertyBar value={material.uv_resistance} label="UV" />
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-2">Ventajas</p>
                      <div className="flex flex-wrap gap-2">
                        {material.pros?.slice(0, 3).map((pro, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{pro}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-2">Temperatura</p>
                      <span className="font-mono">{material.print_temp_min}-{material.print_temp_max}°C</span>
                    </div>
                    {material.requires_enclosure && (
                      <Badge variant="destructive" className="text-xs">Requiere Cerramiento</Badge>
                    )}
                  </div>

                  <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-4">
                    <Button
                      variant="outline"
                      className="btn-industrial rounded-sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/material/${material.id}`); }}
                    >
                      VER DETALLES
                    </Button>
                    {(() => {
                      const buyOptions = getBuyUrls(material);
                      if (buyOptions.length === 0) return null;
                      return (
                        <>
                          {buyOptions.map((opt, optIdx) => (
                            <a
                              key={optIdx}
                              href={opt.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => { e.stopPropagation(); trackAndNavigate(e, material, opt, user); }}
                              className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-sm btn-industrial transition-colors ${opt.primary ? "bg-[#1a8ccc] hover:bg-[#1575b0] text-white" : opt.destination === "esun" ? "bg-[#00a651] hover:bg-[#008c45] text-white" : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"}`}
                              data-testid={`buy-btn-${material.id}-${opt.destination}`}
                            >
                              {opt.label}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </a>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-12 flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate("/wizard")} className="btn-industrial rounded-sm">
            NUEVA BÚSQUEDA
          </Button>
          <Button variant="outline" onClick={() => navigate("/compare")} className="btn-industrial rounded-sm">
            COMPARAR MATERIALES
          </Button>
        </div>
      </div>
    </div>
  );
};

// Catalog Page
// Favorites Page
const FavoritesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/favorites/${user.id}`);
        setMaterials(response.data.materials);
      } catch (error) {
        toast.error("Error al cargar favoritos");
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [user]);

  const removeFavorite = async (materialId) => {
    try {
      await axios.delete(`${API}/favorites/${user.id}/${materialId}`);
      setMaterials(materials.filter(m => m.id !== materialId));
      toast.success("Eliminado de favoritos");
    } catch (error) {
      toast.error("Error al eliminar favorito");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-24 px-6 flex flex-col items-center justify-center" data-testid="favorites-no-auth">
        <Heart className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl mb-4">Inicia sesión para ver tus favoritos</h2>
        <p className="text-muted-foreground mb-8">Guarda tus materiales favoritos para acceder rápidamente</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl mb-4">MIS FAVORITOS</h1>
          <p className="text-muted-foreground">Tus materiales guardados para referencia rápida</p>
        </div>

        {loading ? (
          <div className="catalog-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="loading-skeleton h-64 rounded-sm" />
            ))}
          </div>
        ) : materials.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl mb-2">No tienes favoritos aún</h3>
            <p className="text-muted-foreground mb-8">Explora el catálogo y guarda tus materiales favoritos</p>
            <Button onClick={() => navigate("/catalog")} className="bg-[#1a8ccc] hover:bg-[#1575b0] text-white">
              IR AL CATÁLOGO
            </Button>
          </div>
        ) : (
          <div className="catalog-grid">
            {materials.map((material) => (
              <MaterialCard
                key={material.id}
                material={material}
                onSelect={() => navigate(`/material/${material.id}`)}
                onFavorite={removeFavorite}
                isFavorite={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Page - List all users
// Compare Page
const ComparePage = () => {
  const [materials, setMaterials] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const response = await axios.get(`${API}/materials`);
        // Handle paginated response
        setMaterials(response.data.materials || response.data);
      } catch (error) {
        toast.error("Error al cargar materiales");
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (selected.length > 0) {
      const fetchComparison = async () => {
        try {
          const response = await axios.post(`${API}/compare`, selected);
          setComparison(response.data.materials);
        } catch (error) {
          console.error(error);
        }
      };
      fetchComparison();
    } else {
      setComparison([]);
    }
  }, [selected]);

  const toggleSelect = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else if (selected.length < 3) {
      setSelected([...selected, id]);
    } else {
      toast.warning("Máximo 3 materiales para comparar");
    }
  };

  const properties = [
    { key: "tensile_strength", label: "Resistencia Tracción" },
    { key: "impact_resistance", label: "Resistencia Impactos" },
    { key: "hardness", label: "Dureza" },
    { key: "flexibility", label: "Flexibilidad" },
    { key: "rigidity", label: "Rigidez" },
    { key: "uv_resistance", label: "Resistencia UV" },
    { key: "moisture_resistance", label: "Resistencia Humedad" },
    { key: "chemical_resistance", label: "Resistencia Química" },
    { key: "heat_resistance", label: "Resistencia Térmica" },
    { key: "price_per_kg", label: "Precio (€/kg)" }
  ];

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="compare-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl mb-4">COMPARADOR</h1>
          <p className="text-muted-foreground">Selecciona hasta 3 materiales para comparar</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Material Selector */}
          <div>
            <h3 className="text-lg mb-4">SELECCIONAR MATERIALES</h3>
            <ScrollArea className="h-[500px] border border-border p-4">
              <div className="space-y-2">
                {materials.map((mat) => (
                  <div
                    key={mat.id}
                    className={`p-3 border cursor-pointer transition-colors ${
                      selected.includes(mat.id) ? "border-[#1a8ccc] bg-[#1a8ccc]/5" : "border-border hover:border-foreground"
                    }`}
                    onClick={() => toggleSelect(mat.id)}
                    data-testid={`compare-select-${mat.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{mat.name}</span>
                      {selected.includes(mat.id) && <Check className="w-4 h-4 text-[#1a8ccc]" />}
                    </div>
                    <span className="text-xs text-muted-foreground">{mat.category}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Comparison Table */}
          <div className="lg:col-span-2">
            {comparison.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Propiedad</th>
                      {comparison.map((mat) => (
                        <th key={mat.id}>{mat.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-medium">Categoría</td>
                      {comparison.map((mat) => (
                        <td key={mat.id}>
                          <Badge>{mat.category}</Badge>
                        </td>
                      ))}
                    </tr>
                    {properties.map((prop) => (
                      <tr key={prop.key}>
                        <td className="font-medium">{prop.label}</td>
                        {comparison.map((mat) => (
                          <td key={mat.id} className="font-mono">
                            {prop.key === "price_per_kg" 
                              ? `€${mat[prop.key]}` 
                              : mat[prop.key]?.toFixed(1)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="font-medium">Temperatura Impresión</td>
                      {comparison.map((mat) => (
                        <td key={mat.id} className="font-mono">
                          {mat.print_temp_min}-{mat.print_temp_max}°C
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="font-medium">Requiere Cerramiento</td>
                      {comparison.map((mat) => (
                        <td key={mat.id}>
                          {mat.requires_enclosure ? <Badge variant="destructive">Sí</Badge> : <Badge variant="outline">No</Badge>}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center border border-dashed border-border p-12">
                <p className="text-muted-foreground text-center">
                  Selecciona materiales de la lista para compararlos
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Calculator Page
// Material Detail Page
const MaterialDetailPage = () => {
  const { id } = require("react-router-dom").useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchMaterial = async () => {
      try {
        const response = await axios.get(`${API}/materials/${id}`);
        setMaterial(response.data);
      } catch (error) {
        toast.error("Material no encontrado");
        navigate("/catalog");
      } finally {
        setLoading(false);
      }
    };
    fetchMaterial();
  }, [id, navigate]);

  useEffect(() => {
    const checkFavorite = async () => {
      if (user && token && id) {
        try {
          const response = await axios.get(`${API}/favorites/${user.id}`);
          const favIds = response.data.favorites.map(f => f.material_id);
          setIsFavorite(favIds.includes(id));
        } catch (error) {
          console.error(error);
        }
      }
    };
    checkFavorite();
  }, [user, token, id]);

  const toggleFavorite = async () => {
    if (!user || !token) {
      toast.info("Inicia sesión para guardar favoritos");
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${user.id}/${id}`);
        setIsFavorite(false);
        toast.success("Eliminado de favoritos");
      } else {
        await axios.post(`${API}/favorites`, { user_id: user.id, material_id: id });
        setIsFavorite(true);
        toast.success("Añadido a favoritos");
      }
    } catch (error) {
      toast.error("Error al actualizar favoritos");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-6 flex items-center justify-center">
        <div className="loading-skeleton w-64 h-8" />
      </div>
    );
  }

  if (!material) return null;

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="material-detail-page">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <Badge variant="outline" className="badge-industrial mb-4">
                    {material.category}
                  </Badge>
                  <h1 className="text-4xl mb-2">{material.name}</h1>
                  <p className="text-muted-foreground font-mono">{material.brand}</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleFavorite}
                  className={`shrink-0 ${isFavorite ? "text-red-500 border-red-500" : ""}`}
                  data-testid="detail-favorite-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
              </div>
            </div>

            <p className="text-lg">{material.description}</p>

            <div>
              <h3 className="text-lg mb-4">PROPIEDADES MECÁNICAS</h3>
              <div className="grid grid-cols-2 gap-4">
                <PropertyBar value={material.tensile_strength} label="Resistencia Tracción" />
                <PropertyBar value={material.impact_resistance} label="Resistencia Impactos" />
                <PropertyBar value={material.hardness} label="Dureza" />
                <PropertyBar value={material.flexibility} label="Flexibilidad" />
                <PropertyBar value={material.rigidity} label="Rigidez" />
                <PropertyBar value={material.layer_adhesion} label="Adhesión entre capas" />
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4">PROPIEDADES AMBIENTALES</h3>
              <div className="grid grid-cols-2 gap-4">
                <PropertyBar value={material.uv_resistance} label="Resistencia UV" />
                <PropertyBar value={material.moisture_resistance} label="Resistencia Humedad" />
                <PropertyBar value={material.chemical_resistance} label="Resistencia Química" />
                <PropertyBar value={material.heat_resistance} label="Resistencia Térmica" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg mb-4">VENTAJAS</h3>
                <ul className="space-y-2">
                  {material.pros?.map((pro, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5" />
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg mb-4">DESVENTAJAS</h3>
                <ul className="space-y-2">
                  {material.cons?.map((con, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-500 mt-0.5" />
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Información Técnica</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Precio</span>
                  <span className="font-mono text-xl">€{material.price_per_kg}/kg</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Temp. Extrusor</span>
                  <span className="font-mono">{material.print_temp_min}-{material.print_temp_max}°C</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Temp. Cama</span>
                  <span className="font-mono">{material.bed_temp_min}-{material.bed_temp_max}°C</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Cerramiento</span>
                  <span>{material.requires_enclosure ? "Requerido" : "Opcional"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Cama caliente</span>
                  <span>{material.requires_heated_bed ? "Requerida" : "Opcional"}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Acabado</span>
                  <span className="capitalize">{material.surface_finish}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle>Post-Procesamiento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {material.post_processing?.map((pp, i) => (
                    <Badge key={i} variant="outline">{pp}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle>Aplicaciones Recomendadas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {material.best_for?.map((use, i) => (
                    <Badge key={i} className="capitalize">{use.replace("_", " ")}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {(() => {
              // Check stock status first
              if (material.stock_status === "out_of_stock") {
                return (
                  <div className="space-y-2">
                    <div className="bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-sm p-4 text-center">
                      <p className="text-orange-700 dark:text-orange-300 font-medium">Sin stock en Sicnova</p>
                      <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">Contacta con el proveedor para disponibilidad</p>
                    </div>
                    <a
                      href="https://tienda.sicnova3d.com/333-formlabs-materiales"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackBuyClick(material, 'formlabs', 'https://tienda.sicnova3d.com/333-formlabs-materiales', user?.id)}
                      className="w-full inline-flex items-center justify-center px-4 py-4 text-sm font-medium rounded-sm btn-industrial border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                      data-testid="buy-btn"
                    >
                      VER CATÁLOGO FORMLABS
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                );
              }
              
              if (material.stock_status === "contact_supplier") {
                return (
                  <div className="space-y-2">
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-sm p-4 text-center">
                      <p className="text-yellow-700 dark:text-yellow-300 font-medium">Disponibilidad bajo consulta</p>
                      <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">Contacta con Sicnova para confirmar stock</p>
                    </div>
                    {material.sicnova_url && (
                      <a
                        href={material.sicnova_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackBuyClick(material, 'sicnova', material.sicnova_url, user?.id)}
                        className="w-full inline-flex items-center justify-center px-4 py-6 text-sm font-medium rounded-sm btn-industrial bg-[#1a8ccc] hover:bg-[#1575b0] text-white transition-colors"
                        data-testid="buy-btn"
                      >
                        CONSULTAR EN SICNOVA
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    )}
                  </div>
                );
              }

              const buyOptions = getBuyUrls(material);
              if (buyOptions.length === 0) return null;
              return (
                <div className="space-y-2">
                  {buyOptions.map((opt, optIdx) => {
                    const isEsun = opt.destination === "esun";
                    const isBambu = opt.destination === "bambulab";
                    const colorClass = opt.primary
                      ? (isEsun ? "bg-[#00a651] hover:bg-[#008c45]" : "bg-[#00a651] hover:bg-[#008c45]")
                      : (isEsun ? "bg-[#00a651] hover:bg-[#008c45]" : isBambu ? "bg-[#1a8ccc] hover:bg-[#1575b0]" : "bg-[#1a8ccc] hover:bg-[#1575b0]");
                    return (
                      <a
                        key={optIdx}
                        href={opt.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => trackAndNavigate(e, material, opt, user)}
                        className={`w-full inline-flex items-center justify-center px-4 ${opt.primary ? "py-6" : "py-4"} text-sm font-medium rounded-sm btn-industrial transition-colors ${colorClass} text-white`}
                        data-testid={`buy-btn-${opt.destination}`}
                      >
                        {opt.label}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
